import { useState, useEffect } from 'react';
import { fmt } from '../lib/portfolio';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

// Compares actual holdings against a saved plan; renders a drift banner.
export default function PlanReminder({ actualMatrix, savedPlan, meta, entities, savedAt, compact }) {
  // Persist dismissal per saved-plan version so it survives the 10s data poll /
  // remounts, but re-shows after a fresh save (savedAt changes the key).
  const dismissKey = `planDismiss:${savedAt || 'none'}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(dismissKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { setDismissed(sessionStorage.getItem(`planDismiss:${savedAt || 'none'}`) === '1'); } catch { /* ignore */ }
  }, [savedAt]);
  const dismiss = () => { try { sessionStorage.setItem(dismissKey, '1'); } catch { /* ignore */ } setDismissed(true); };
  if (!savedPlan || !actualMatrix || !meta?.length) return null;
  const planned = savedPlan.planned || {};

  const assetTotal = (mx, a) => entities.reduce((s, e) => s + (mx[e]?.[a] || 0), 0);
  const networth = meta.reduce((s, m) => s + assetTotal(actualMatrix, m.asset), 0) || 1;
  const TH = 25000; // flat ₹25k floor — matches the per-cell buy/sell arrows

  // Drift uses target % from plan applied to TODAY's networth, not the absolute
  // plan rupees. Markets can drift the total up/down without faking buy/sell
  // signals; only true allocation skew shows.
  const plannedNW = entities.reduce((s, e) => s + meta.reduce((ss, m) => ss + (planned[e]?.[m.asset] || 0), 0), 0) || 1;
  const scale = networth / plannedNW;
  const baseline = savedPlan.baseline; // present on plans saved after this fix; undefined on older plans

  // Per-cell (member × asset) drift — matches the Actuals/Planning buy/sell arrows,
  // so a member-level reshuffle (asset totals unchanged) still flags as off-plan.
  const drifts = meta.map(m => {
    let net = 0, any = false;
    for (const e of entities) {
      // Skip cells the user did not intentionally re-allocate at plan-save time.
      // Without a baseline (old plans) we fall back to full-cell drift.
      if (baseline && baseline[e]?.[m.asset] === planned[e]?.[m.asset]) continue;
      const target = (planned[e]?.[m.asset] || 0) * scale;
      const d = target - (actualMatrix[e]?.[m.asset] || 0);
      if (Math.abs(d) > TH) { net += d; any = true; }
    }
    return { label: m.label, delta: net, any };
  }).filter(d => d.any);

  if (drifts.length === 0) {
    if (dismissed) return null; // on-plan notice is dismissable
    return (
      <div className="flex items-center gap-3 rounded-2xl px-5 py-3.5 mb-5" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)' }}>
        <CheckCircle2 size={20} color="#34d399" />
        <span style={{ color: '#34d399', fontSize: '15px', fontWeight: 600 }}>On plan</span>
        <span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>holdings match your saved plan</span>
        <button onClick={dismiss} className="ml-auto" style={{ color: 'var(--text-mute)' }} title="Dismiss">
          <X size={17} />
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl px-5 py-4 mb-5" style={{ background: 'rgba(251,146,60,0.09)', border: '1px solid rgba(251,146,60,0.28)' }}>
      <div className="flex items-center gap-3 mb-1">
        <AlertTriangle size={20} color="#fb923c" />
        <span style={{ color: '#fb923c', fontSize: '16px', fontWeight: 700 }}>Follow your plan — {drifts.length} position{drifts.length > 1 ? 's' : ''} off target</span>
      </div>
      <p style={{ color: 'var(--text-dim)', fontSize: '14px', marginLeft: '32px' }}>
        {drifts.map(d => Math.abs(d.delta) > TH ? `${d.delta > 0 ? 'Buy' : 'Sell'} ${fmt(Math.abs(d.delta))} ${d.label}` : `Rebalance ${d.label} between members`).join('  ·  ')}
        {!compact && savedAt ? `   (plan saved ${new Date(savedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })})` : ''}
      </p>
    </div>
  );
}
